import { FC, useContext, useEffect, useState } from 'react';

import { ContextMenu, Table, useContextMenu } from '@smartshell/ui-kit';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import { LayoutContext } from 'components/Layout';
import { useContextMenuItemsGoods, useGoodsRequests } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { TGood } from 'types';

import { IGoodsGoodTableProps } from '../interfaces';
import s from './GoodsGoodTable.module.scss';
import { useColumns } from './useColumns';

export const GoodsGoodTable: FC<IGoodsGoodTableProps> = ({
  filteredGoods,
  loadedCategories,
  setAmountBalances,
  openAmountEditor,
  groupFiltered,
  setIsDeletingError,
  loadingTable,
  setDeletingMessage,
  setAmountReport,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { shellGoodsDisplayEnabled },
    },
  } = useContext(SettingsContext);
  // const isAvailable = useIsFeatureAvailable('shell_goods_display');

  const [selectedGoods, setSelectedGoods] = useState<TGood[]>([]);
  const layoutElement = useContext(LayoutContext);
  const [lastSelectedRowIndex, setLastSelectedRowIndex] = useState<
    number | null
  >(null);
  const {
    loadedGoods,
    deleteGood,
    toggleShowInShell,
    updateGood,
    deleteGoods,
    updateGoods,
  } = useGoodsRequests({ setIsDeletingError });

  const contextMenuItems = useContextMenuItemsGoods({
    deleteGood,
    toggleShowInShell,
    updateGood,
    openAmountEditor,
    show_in_shell: selectedGoods.some((good) => good.show_in_shell),
    isGroup: selectedGoods[0]?.category || null,
    loadedCategories,
    setIsDeletingError,
    selectedGoods,
    deleteGoods,
    updateGoods,
    setDeletingMessage,
    setAmountReport,
  });

  const {
    contextMenuData,
    closeContextMenu,
    onContextMenu,
    cursorPosition,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = useContextMenu<TGood[]>();

  useEffect(() => {
    const totalAmountBalances = loadedGoods.reduce((sum, good) => {
      return sum + good.cost * good.amount;
    }, 0);

    setSelectedGoods([]);

    setSelectedGoods((prevSelectedGoods) =>
      prevSelectedGoods.filter((good: TGood) =>
        loadedGoods.some((loadedGood: TGood) => loadedGood?.id === good?.id)
      )
    );

    setAmountBalances(totalAmountBalances);
  }, [loadedGoods, setAmountBalances]);

  const columns = useColumns({ toggleShowInShell, updateGoods });
  const navigate = useNavigate();

  return (
    <div className={s.wrapper}>
      {contextMenuData && cursorPosition && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuData}
          items={contextMenuItems}
          closeContextMenu={closeContextMenu}
          position={cursorPosition}
        />
      )}
      <Table
        data={filteredGoods}
        columns={columns}
        onRowClick={(event, row, table) => {
          const flatRows = table.flatRows;
          const currentRowIndex = flatRows.findIndex((r) => r?.id === row?.id);

          if (event.button === 2) {
            if (!row.isSelected) {
              flatRows.forEach((r) => r.toggleRowSelected(false));
              row.toggleRowSelected(true);
              setSelectedGoods([row.original]);
              setLastSelectedRowIndex(currentRowIndex);
            }

            return;
          }

          if (event.shiftKey) {
            if (lastSelectedRowIndex === null) {
              setLastSelectedRowIndex(currentRowIndex);
            }

            const safeLastSelectedRowIndex =
              lastSelectedRowIndex ?? currentRowIndex;
            const startIndex = Math.min(
              safeLastSelectedRowIndex,
              currentRowIndex
            );
            const endIndex = Math.max(
              safeLastSelectedRowIndex,
              currentRowIndex
            );

            const rowsToSelect = flatRows.slice(startIndex, endIndex + 1);

            flatRows.forEach((r) => r.toggleRowSelected(false));
            rowsToSelect.forEach((r) => r.toggleRowSelected(true));

            setSelectedGoods(rowsToSelect.map((row) => row.original));
          } else if (event.ctrlKey && event.button === 0) {
            if (!event.shiftKey) setLastSelectedRowIndex(currentRowIndex);

            row.toggleRowSelected(!row.isSelected);

            setSelectedGoods((goods) =>
              row.isSelected
                ? goods.filter((item) => item?.id !== row?.original?.id)
                : goods.concat(row.original)
            );
          } else if (event.button === 0) {
            flatRows.forEach((r) => r.toggleRowSelected(false));
            row.toggleRowSelected(true);

            setSelectedGoods([row.original]);
            setLastSelectedRowIndex(currentRowIndex);
          }
        }}
        onRowDoubleClick={(event, row) => {
          navigate(`/goods/${row?.id}`);
        }}
        onContextMenu={(event) => {
          onContextMenu(event, selectedGoods);
        }}
        onTouchStart={(event, row) => {
          setSelectedGoods([row]);
          onTouchStart(event, [row]);
        }}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className={cn(s.table, !shellGoodsDisplayEnabled && s.last_cell)}
        emptyDataPlaceholder={
          groupFiltered
            ? t('goods.table.empty.group')
            : t('goods.table.empty.goods')
        }
        isLoading={loadingTable}
      />
    </div>
  );
};
